import type { NeedleInspectorAPI } from "./api.js";

export interface LiveSocket {
    onopen: WebSocket["onopen"];
    onmessage: WebSocket["onmessage"];
    onclose: WebSocket["onclose"];
    send(value: string): void;
    close(): void;
}

type LiveAPI = Pick<NeedleInspectorAPI, "getTools" | "callTool">;

/** The page opts in by supplying a session; the server can invoke listed tools only. */
export function attachLiveInspection(
    session: string | null,
    api: LiveAPI,
    connect: () => LiveSocket,
    checkSession?: () => Promise<boolean>,
    isActive: () => boolean = () => true,
): { dispose(): void } | null {
    if (!session) return null;
    let disposed = false;
    let socket: LiveSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
    let checkingSession = false;
    let expired = false;

    function stopHeartbeat(): void {
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        heartbeatTimer = undefined;
    }

    function open(): void {
        if (disposed || expired || !isActive() || socket || checkingSession) return;
        const current = connect();
        socket = current;
        current.onopen = () => {
            current.send(JSON.stringify({ type: "heartbeat" }));
            heartbeatTimer = setInterval(() => current.send(JSON.stringify({ type: "heartbeat" })), 60_000);
        };
        current.onmessage = event => { void handle(event.data, current); };
        current.onclose = event => {
            stopHeartbeat();
            if (socket === current) socket = undefined;
            if (disposed || event.code === 4001) {
                if (event.code === 4001) expired = true;
                return;
            }
            if (!isActive()) return;
            checkingSession = true;
            void (async () => {
                try {
                    if (checkSession && !await checkSession()) { expired = true; return; }
                } catch (error) {
                    console.warn("[Needle Inspector Agent] Session check failed; retrying connection.", error);
                } finally {
                    checkingSession = false;
                }
                if (!disposed && !expired && isActive()) reconnectTimer = setTimeout(() => {
                    reconnectTimer = undefined;
                    open();
                }, 1_000);
            })();
        };
    }

    function syncActivity(): void {
        if (disposed || expired) return;
        if (isActive()) {
            if (!socket && !reconnectTimer && !checkingSession) open();
            return;
        }
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
        stopHeartbeat();
        if (socket) {
            const current = socket;
            socket = undefined;
            current.onclose = null;
            current.onopen = null;
            current.close();
        }
    }

    async function handle(raw: string, current: LiveSocket): Promise<void> {
        let message: { id?: unknown; type?: unknown; name?: unknown; arguments?: unknown };
        try { message = JSON.parse(raw); }
        catch { return; }
        if (typeof message.id !== "string" || !message.id || (message.type !== "tools" && message.type !== "call")) return;
        const id = message.id;
        try {
            const tools = await api.getTools();
            if (message.type === "tools") {
                current.send(JSON.stringify({ id, result: tools }));
                return;
            }
            if (typeof message.name !== "string" || !tools.some(tool => tool.name === message.name)) {
                throw new Error("Inspector tool is unavailable in this page.");
            }
            const args = message.arguments;
            if (args !== undefined && (typeof args !== "object" || args === null || Array.isArray(args))) {
                throw new Error("Tool arguments must be an object.");
            }
            const result = await api.callTool(message.name, args as object | undefined);
            current.send(JSON.stringify({ id, result }));
        } catch (error) {
            current.send(JSON.stringify({ id, error: error instanceof Error ? error.message : String(error) }));
        }
    }

    const activityTimer = setInterval(syncActivity, 250);
    syncActivity();
    return { dispose() {
        disposed = true;
        clearInterval(activityTimer);
        if (reconnectTimer) clearTimeout(reconnectTimer);
        stopHeartbeat();
        if (socket) {
            socket.onclose = null;
            socket.close();
        }
    } };
}
